import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Clock, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function RecentTasks({ theme }) {
  const [recentTasks, setRecentTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecentTasks();
  }, []);

  const loadRecentTasks = async () => {
    try {
      const response = await api.get(`/api/tasks/recent`);
      setRecentTasks(response.data);
    } catch (error) {
      console.error('Failed to load recent tasks:', error);
      toast.error('Failed to load recent tasks');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'done') return 'text-[#81B29A] bg-[#81B29A]/10';
    if (status === 'in_progress') return 'text-[#F2CC8F] bg-[#F2CC8F]/20';
    return 'text-stone-400 bg-stone-100';
  };

  const getStatusLabel = (status) => {
    if (status === 'done') return 'Done';
    if (status === 'in_progress') return 'In Progress';
    return 'To Do';
  };

  if (loading) {
    return (
      <div className="bg-white border border-stone-200 rounded-xl p-6 sm:p-8 shadow-sm">
        <div className="animate-pulse">
          <div className="h-6 bg-stone-200 rounded w-32 mb-4"></div>
          <div className="space-y-3">
            <div className="h-12 bg-stone-100 rounded"></div>
            <div className="h-12 bg-stone-100 rounded"></div>
            <div className="h-12 bg-stone-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`${theme?.colors.surface || 'bg-white'} border ${theme?.colors.border || 'border-stone-200'} rounded-xl p-6 sm:p-8 shadow-sm`}
      data-testid="recent-tasks-widget"
    >
      <div className="flex items-center gap-3 mb-6">
        <Clock className={`w-6 h-6 ${theme?.colors.accent || 'text-[#E07A5F]'}`} />
        <h3 
          className={`text-xl font-medium ${theme?.colors.textPrimary || 'text-stone-900'}`}
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          Recent Tasks
        </h3>
      </div>

      {recentTasks.length === 0 ? (
        <p 
          className={`${theme?.colors.textSecondary || 'text-stone-400'} text-center py-8`}
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          No tasks yet. Create your first task above!
        </p>
      ) : (
        <div className="space-y-3">
          {recentTasks.map((task) => (
            <div
              key={task.task_id}
              className={`flex items-center justify-between p-4 ${theme?.colors.bg || 'bg-stone-50'} rounded-lg hover:opacity-80 transition-colors cursor-pointer group`}
              data-testid={`recent-task-${task.task_id}`}
            >
              <div className="flex-1 min-w-0">
                <h4 
                  className={`font-medium ${theme?.colors.textPrimary || 'text-stone-900'} truncate mb-1`}
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {task.title}
                </h4>
                <span 
                  className={`text-xs px-2 py-1 rounded-full font-medium inline-block ${
                    getStatusColor(task.status)
                  }`}
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  {getStatusLabel(task.status)}
                </span>
              </div>
              <ChevronRight className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors flex-shrink-0" />
            </div>
          ))}
        </div>
      )}

      <p 
        className="text-xs text-stone-400 text-center mt-4"
        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
      >
        Your last 3 created tasks
      </p>
    </div>
  );
}

export default RecentTasks;